import React, { useRef, useState } from "react";
import { Field, FieldArray } from "redux-form";
import { IconButton } from "@mui/material";

import { customInput } from "../../../../../common/custom-input";
import { customTextarea } from "../../../../../common/customTextarea";
import styles from "../../../SwmsMastersStyle.module.css";
import layoutStyles from "../../EmergencyPlansLayout.module.css";
import v2Styles from "../../../../v2Styles.module.css";

const getDefaultAlarmItem = () => ({
  element: "",
  required_arrangement: ""
});

function AlarmRows({ fields, loading, dragHandleOnly }) {
  const dragIndexRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div>
      {fields.length === 0 ? (
        <div className={layoutStyles.RolesEmptyState}>No elements added yet.</div>
      ) : null}

      {fields.length > 0 ? (
        <div className={layoutStyles.RolesRowsContainer}>
          {fields.map((member, index) => (
            <div
              key={member}
              className={`${layoutStyles.RolesRow} ${layoutStyles.AlarmRow}`}
              draggable={!loading && !dragHandleOnly}
              onDragStart={(event) => {
                if (loading) {
                  event.preventDefault();
                  return;
                }
                dragIndexRef.current = index;
                setDraggingIndex(index);
                setHoveredIndex(null);
                if (event.dataTransfer) {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", `${index}`);
                }
              }}
              onDragEnter={(event) => {
                if (loading || draggingIndex === null || draggingIndex === index) {
                  return;
                }
                event.preventDefault();
                setHoveredIndex(index);
              }}
              onDragOver={(event) => {
                if (loading || dragIndexRef.current === null || draggingIndex === index) {
                  return;
                }
                event.preventDefault();
                if (hoveredIndex !== index) {
                  setHoveredIndex(index);
                }
                if (event.dataTransfer) {
                  event.dataTransfer.dropEffect = "move";
                }
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setHoveredIndex((current) => (current === index ? null : current));
                }
              }}
              onDrop={(event) => {
                if (loading) {
                  return;
                }
                event.preventDefault();
                const fromIndex = dragIndexRef.current;
                if (fromIndex === null || fromIndex === index) {
                  dragIndexRef.current = null;
                  setDraggingIndex(null);
                  setHoveredIndex(null);
                  return;
                }
                fields.move(fromIndex, index);
                dragIndexRef.current = null;
                setDraggingIndex(null);
                setHoveredIndex(null);
              }}
              onDragEnd={() => {
                dragIndexRef.current = null;
                setDraggingIndex(null);
                setHoveredIndex(null);
              }}
              style={{
                opacity: draggingIndex === index ? 0.55 : 1,
                borderTop: hoveredIndex === index && draggingIndex !== index ? "2px solid #7C3AED" : "2px solid transparent"
              }}
            >
              <span
                className={`${layoutStyles.RolesDragHandle} ${layoutStyles.AlarmDragHandle}`}
                draggable={!loading && dragHandleOnly}
                onDragStart={(event) => {
                  if (!dragHandleOnly) {
                    return;
                  }
                  if (loading) {
                    event.preventDefault();
                    return;
                  }
                  event.stopPropagation();
                  dragIndexRef.current = index;
                  setDraggingIndex(index);
                  setHoveredIndex(null);
                  if (event.dataTransfer) {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", `${index}`);
                  }
                }}
                onDragEnd={() => {
                  if (dragHandleOnly) {
                    dragIndexRef.current = null;
                    setDraggingIndex(null);
                    setHoveredIndex(null);
                  }
                }}
                style={{ cursor: loading ? "default" : (draggingIndex === index ? "grabbing" : "grab") }}
                title="Drag to reorder element"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="12" height="12">
                  <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
                </svg>
              </span>

              <div className={layoutStyles.RolesInputColumn}>
                <Field
                  name={`${member}.element`}
                  component={customInput}
                  className={styles.TextField}
                  disabled={loading}
                  placeholder="Element"
                  autoComplete="off"
                />
              </div>

              <div className={layoutStyles.AlarmArrangementColumn}>
                <Field
                  name={`${member}.required_arrangement`}
                  component={customTextarea}
                  className={`${v2Styles.TextArea} ${v2Styles.AutoHeight} ${v2Styles.Rows2}`}
                  disabled={loading}
                  placeholder="Required Arrangement"
                  rows={2}
                  autoComplete="off"
                />
              </div>

              <IconButton
                className={`p-0 ${layoutStyles.IconButtonAutoHeight} ${layoutStyles.AlarmDeleteButton}`}
                onClick={() => fields.remove(index)}
                disabled={loading}
                title="Delete element"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`${styles.DeleteIcon} ${layoutStyles.MediaDeleteIcon}`}>
                  <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
                </svg>
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        className={`${layoutStyles.AddSectionButton} ${layoutStyles.RolesAddButton}`}
        onClick={() => fields.push(getDefaultAlarmItem())}
        disabled={loading}
      >
        <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
          <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
        </svg>
        Add Element
      </button>
    </div>
  );
}

export default function AlarmCommunicationEmergencyAccessSection({ loading, dragHandleOnly = false }) {
  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          <div className={layoutStyles.RolesColumnHeadings}>
            <span />
            <span>Element</span>
            <span style={{ flex: "2 1 0" }}>Required Arrangement</span>
            <span />
          </div>
          <FieldArray name="items" component={AlarmRows} loading={loading} dragHandleOnly={dragHandleOnly} />
        </div>
      </div>
    </div>
  );
}
